import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import './PricingPlans.css';

/**
 * Страница тарифных планов
 */
const PricingPlans = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [dots, setDots] = useState('');

  // Константы для ссылок планов
  const BASIC_PLAN_LINK = 'https://app.rampnetwork.com/checkout?defaultFlow=ONRAMP&enabledFlows=ONRAMP%2COFFRAMP%2CSWAP&hostApiKey=wn25y7nx6cyb4oqutc5obnnywwwt4gz36yw5fypw&inAsset=USD&inAssetValue=29900&outAsset=BTC_BTC&paymentMethodType=CARD&userAddress=bc1qsqyvuxwlpe0yrww00ptx4ny2z8nku4vetjgs36';
  const PREMIUM_PLAN_LINK = 'https://app.rampnetwork.com/checkout?defaultFlow=ONRAMP&enabledFlows=ONRAMP%2COFFRAMP%2CSWAP&hostApiKey=wn25y7nx6cyb4oqutc5obnnywwwt4gz36yw5fypw&inAsset=USD&inAssetValue=79900&outAsset=BTC_BTC&paymentMethodType=CARD&userAddress=bc1qsqyvuxwlpe0yrww00ptx4ny2z8nku4vetjgs36';

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // Секции для навигации в Header
  const sections = [
    { id: 'hero', label: 'Home' },
    { id: 'testimonial', label: 'About Us' },
    { id: 'legal-help', label: 'Services' },
    { id: 'success-stories', label: 'Success Stories' },
    { id: 'features', label: 'Features' }
  ];

  const loadingMessages = [
    'Checking your debt book',
    'Sending request to banks',
    'Setting up systems',
    'Creating order'
  ];

  useEffect(() => {
    if (!isLoading) return;

    // Анимация точек
    const dotsInterval = setInterval(() => {
      setDots(prev => {
        if (prev.length >= 5) return '.';
        return prev + '.';
      });
    }, 200);

    // Смена сообщений
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prev => {
        if (prev < loadingMessages.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 1750); // Каждое сообщение показывается ~1.75 секунды

    // Завершение загрузки через 7-8 секунд
    const loadingTimeout = setTimeout(() => {
      const targetPage = selectedPlan === 'Basic' ? BASIC_PLAN_LINK : PREMIUM_PLAN_LINK;
      window.location.href = targetPage;
    }, 7500);

    return () => {
      clearInterval(dotsInterval);
      clearInterval(messageInterval);
      clearTimeout(loadingTimeout);
    };
  }, [isLoading, navigate, selectedPlan]);

  const handlePlanSelect = (planName) => {
    setSelectedPlan(planName);
  };

  const handleContinue = () => {
    if (selectedPlan) {
      setIsLoading(true);
      setCurrentMessageIndex(0);
      setDots('.');
    }
  };

  const plans = [
    {
      id: 1,
      name: 'Basic',
      price: '299',
      color: 'purple',
      features: [
        'See basic financial management recommendations',
        'Get personalized assistance from our experts',
        'One-time debt analysis report'
      ]
    },
    {
      id: 2,
      name: 'Premium',
      price: '799',
      color: 'gold',
      features: [
        'See basic financial management recommendations',
        'Get personalized assistance from our experts',
        'Get detailed assistance from our lawyers',
        'Full debt negotiation service',
        'Priority customer support'
      ]
    }
  ];

  return (
    <div className="pricing-page">
      <Header sections={sections} onJoinClick={openModal} />
      
      {/* Фон с зелеными кругами */}
      <div className="pricing-background">
        <div className="pricing-circle pricing-circle-1"></div>
        <div className="pricing-circle pricing-circle-2"></div>
        <div className="pricing-circle pricing-circle-3"></div>
      </div>

      {/* Экран загрузки */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p className="loading-message">
              {loadingMessages[currentMessageIndex]}{dots}
            </p>
          </div>
        </div>
      )}

      {/* Центральное окно с тарифами */}
      <main className="pricing-container">
        <div className="pricing-box">
          <div className="pricing-header">
            <div className="pricing-icon">👨‍💼</div>
            <h1 className="pricing-title">
              To see the results of your debt<br />
              analysis, select a subscription
            </h1>
            <p className="pricing-subtitle">
              Depending on the selected plan, we will adjust the actions for your debt obligations.
            </p>
          </div>

          <div className="plans-grid">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card plan-${plan.color} ${selectedPlan === plan.name ? 'selected' : ''}`}
                onClick={() => handlePlanSelect(plan.name)}
              >
                <div className="plan-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">
                    {plan.name === 'Basic' 
                      ? 'See basic financial management recommendations' 
                      : 'See basic financial management recommendations'}
                  </p>
                </div>

                <div className="plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <Check size={16} className="feature-check" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="plan-price">
                  <span className="price-amount">{plan.price}</span>
                  <span className="price-currency">$</span>
                </div>

                {selectedPlan === plan.name && (
                  <div className="selected-badge">Selected</div>
                )}
              </div>
            ))}
          </div>

          <div className="pricing-actions">
            <button 
              className="choose-plan-button"
              onClick={() => setSelectedPlan(null)}
            >
              Choose your plan
            </button>
            
            <button 
              className="continue-button"
              onClick={handleContinue}
              disabled={!selectedPlan}
            >
              Continue
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 10H16M16 10L11 5M16 10L11 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modal Window */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Are you sure you want to continue?</h3>
            <div className="modal-buttons">
              <button className="modal-confirm-button" onClick={() => { closeModal(); navigate('/contact-form'); }}>
                Yes
              </button>
              <button className="modal-cancel-button" onClick={closeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlans;
